"use client";

import { cn } from "@newxsystems/shared";
import { Icon } from "@templates/design-system";
import { useEffect, useRef, useState } from "react";
import { getComponent } from "../../../../client";
import { GroundAction } from "../GroundAction/GroundAction";
import { GroundMedia } from "../GroundMedia/GroundMedia";
import type { IMegaMenuNavigationProps } from "./MegaMenuNavigation.props";

export const MegaMenuNavigation = ({ actions, items, logo, sticky }: IMegaMenuNavigationProps) => {
  const Link = getComponent("Link");
  const [openDesktop, setOpenDesktop] = useState<number | null>(null);
  const [openMobile, setOpenMobile] = useState<number | null>(null);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const headerRef = useRef<HTMLElement>(null);
  const dialogRef = useRef<HTMLDialogElement>(null);

  useEffect(() => {
    const close = (event: MouseEvent) => {
      if (!headerRef.current?.contains(event.target as Node)) setOpenDesktop(null);
    };
    const escape = (event: KeyboardEvent) => {
      if (event.key === "Escape") setOpenDesktop(null);
    };
    document.addEventListener("mousedown", close);
    document.addEventListener("keydown", escape);
    return () => {
      document.removeEventListener("mousedown", close);
      document.removeEventListener("keydown", escape);
    };
  }, []);

  useEffect(() => {
    const dialog = dialogRef.current;
    if (!dialog) return;
    if (drawerOpen && !dialog.open) dialog.showModal();
    if (!drawerOpen && dialog.open) dialog.close();
  }, [drawerOpen]);
  const navigate = () => {
    setDrawerOpen(false);
    setOpenMobile(null);
  };
  const linkClass =
    "inline-flex min-h-11 items-center px-3 font-ground-display text-sm font-bold text-foreground transition-colors hover:text-primary focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring";
  const nestedLink = (
    link: {
      accessibilityLabel?: string | null;
      href: string;
      label: string;
      openInNewTab?: boolean | null;
    },
    key: number,
    mobile = false,
  ) => (
    <Link
      aria-label={link.accessibilityLabel || undefined}
      className={cn(linkClass, "w-full")}
      href={link.href}
      key={key}
      onClick={mobile ? navigate : undefined}
      rel={link.openInNewTab ? "noopener noreferrer" : undefined}
      target={link.openInNewTab ? "_blank" : undefined}
    >
      {link.label}
    </Link>
  );

  if (!logo && !items?.length && !actions?.length) return null;

  return (
    <header
      className={cn(
        "z-40 border-b border-weathered-steel bg-mineral-gray",
        sticky && "sticky top-0",
      )}
      ref={headerRef}
    >
      <div className="ground-container flex min-h-20 items-center gap-6">
        {logo ? (
          <Link
            aria-label="Home"
            className="shrink-0 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring"
            href="/"
          >
            <GroundMedia
              className="h-11 w-48 bg-transparent"
              imageClassName="object-contain object-left"
              media={logo}
              sizes="192px"
            />
          </Link>
        ) : null}
        <nav aria-label="Primary" className="ml-auto hidden items-center gap-1 lg:flex">
          {items?.map((item, index) =>
            item.blockType === "megaMenuDirectLink" ? (
              nestedLink(item.link, index)
            ) : (
              <div className="relative" key={index}>
                <button
                  aria-expanded={openDesktop === index}
                  className={cn(linkClass, "cursor-pointer gap-1")}
                  onClick={() => setOpenDesktop(openDesktop === index ? null : index)}
                  type="button"
                >
                  {item.label}
                  <Icon
                    className={cn(
                      "size-4 transition-transform",
                      openDesktop === index && "rotate-180",
                    )}
                    name="chevron-down"
                  />
                </button>
                {openDesktop === index ? (
                  <div
                    className={cn(
                      "absolute right-0 top-full z-50 mt-px min-w-64 border border-weathered-steel bg-mineral-gray p-2",
                      item.blockType === "megaMenuGroup" &&
                        "grid min-w-[36rem] grid-cols-2 gap-4 p-4",
                    )}
                  >
                    {item.blockType === "megaMenuDropdown"
                      ? item.links.map((link, linkIndex) => nestedLink(link, linkIndex))
                      : item.groupItems.map((group, groupIndex) => (
                          <div key={groupIndex}>
                            <p className="border-b border-weathered-steel px-3 py-2 font-ground-mono text-xs text-primary">
                              {group.header}
                            </p>
                            {group.links.map((link, linkIndex) => nestedLink(link, linkIndex))}
                          </div>
                        ))}
                  </div>
                ) : null}
              </div>
            ),
          )}
        </nav>
        {actions?.length ? (
          <div className="hidden gap-2 lg:flex">
            {actions.map((action, index) => (
              <GroundAction action={action} key={index} />
            ))}
          </div>
        ) : null}
        <button
          aria-controls="ground-mobile-navigation"
          aria-expanded={drawerOpen}
          aria-label={drawerOpen ? "Close navigation menu" : "Open navigation menu"}
          className="ml-auto inline-flex size-11 items-center justify-center border border-weathered-steel focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring lg:hidden"
          onClick={() => setDrawerOpen(true)}
          type="button"
        >
          <Icon name="menu" />
        </button>
      </div>
      <dialog
        aria-label="Site navigation"
        className="m-0 ml-auto h-dvh max-h-none w-full max-w-none bg-transparent p-0 backdrop:bg-charcoal-green-ink/45 md:w-[28rem]"
        id="ground-mobile-navigation"
        onClose={() => setDrawerOpen(false)}
        ref={dialogRef}
      >
        <div className="flex h-full w-full flex-col bg-mineral-gray p-6">
          <div className="flex items-center justify-between border-b border-weathered-steel pb-4">
            {logo ? (
              <Link
                aria-label="Home"
                className="focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring"
                href="/"
                onClick={navigate}
              >
                <GroundMedia
                  className="h-10 w-44 bg-transparent"
                  imageClassName="object-contain object-left"
                  media={logo}
                  sizes="176px"
                />
              </Link>
            ) : (
              <span className="font-ground-mono text-xs">Menu</span>
            )}
            <button
              aria-label="Close navigation menu"
              className="inline-flex size-11 items-center justify-center focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring"
              onClick={() => setDrawerOpen(false)}
              type="button"
            >
              <Icon name="x" />
            </button>
          </div>
          <nav aria-label="Mobile primary" className="flex-1 overflow-y-auto py-4">
            {items?.map((item, index) =>
              item.blockType === "megaMenuDirectLink" ? (
                nestedLink(item.link, index, true)
              ) : (
                <div className="border-b border-weathered-steel" key={index}>
                  <button
                    aria-expanded={openMobile === index}
                    className={cn(linkClass, "w-full cursor-pointer justify-between")}
                    onClick={() => setOpenMobile(openMobile === index ? null : index)}
                    type="button"
                  >
                    {item.label}
                    <Icon
                      className={cn(
                        "size-4 transition-transform",
                        openMobile === index && "rotate-180",
                      )}
                      name="chevron-down"
                    />
                  </button>
                  {openMobile === index ? (
                    <div className="pb-3 pl-3">
                      {item.blockType === "megaMenuDropdown"
                        ? item.links.map((link, linkIndex) => nestedLink(link, linkIndex, true))
                        : item.groupItems.map((group, groupIndex) => (
                            <div key={groupIndex}>
                              <p className="px-3 py-2 font-ground-mono text-xs text-primary">
                                {group.header}
                              </p>
                              {group.links.map((link, linkIndex) =>
                                nestedLink(link, linkIndex, true),
                              )}
                            </div>
                          ))}
                    </div>
                  ) : null}
                </div>
              ),
            )}
          </nav>
          {actions?.length ? (
            <div className="flex flex-col gap-2 border-t border-weathered-steel pt-4">
              {actions.map((action, index) => (
                <GroundAction action={action} key={index} />
              ))}
            </div>
          ) : null}
        </div>
      </dialog>
    </header>
  );
};

MegaMenuNavigation.displayName = "Content::MegaMenuNavigation";
